import React, { useState, useEffect, useRef, useCallback } from 'react';
import HVHSViz from './hvhsvisualizer';
import CountryTypeahead from './CountryTypeahead';
import BenchmarkingMatrix from './BenchmarkingMatrix';

const HVHS_COUNTRY_COVERAGE = 193;
const HVHS_DATA_UPDATED = 'July 2026';
// Country Profile opens on this country on first load (exact spelling from country_list.txt).
const DEFAULT_PROFILE_COUNTRY = 'United States of America';

// Framework details shown in the ⓘ pop-over (same paragraph as the About view, acronym spelled out).
const FRAMEWORK_PARAGRAPH = 'The Harvard high value health system model consists of 10 interdependent and mutually reinforcing design components that characterize the ongoing transition in health systems towards a ‘value’ predominant system orientation. The framework assesses health systems across these components — including governance, financing, service delivery, health workforce, information systems, and more — to provide a holistic view of health system performance and identify opportunities for improvement.';

const PAGE_COPY = {
  map: {
    title: 'Global High Value Health System Score Map',
    description: 'Compare overall or component level high value health system scores across countries on a global map. Higher scores indicate a stronger transition towards a high value health system.'
  },
  profile: {
    title: 'Country Profile on High Value Health Systems',
    description: 'Explore this country’s high value health system component and element scores. Use Show Data and Show Metadata to review the values, sources, definitions, and coverage used in scoring.'
  },
  about: {
    title: 'About the Project',
    description: 'Review the project purpose, methods, scoring framework, data sources, and limitations.'
  }
};

const backButtonStyle = {
  padding: '0.625rem 1.25rem', backgroundColor: '#6B7280', color: 'white',
  border: 'none', borderRadius: '8px', fontSize: '0.9375rem', fontWeight: '500',
  cursor: 'pointer', transition: 'background-color 0.2s', whiteSpace: 'nowrap',
  display: 'flex', alignItems: 'center', gap: '0.5rem'
};

const linkButtonStyle = {
  background: 'none', border: 'none', padding: 0, color: '#113743', fontWeight: '600',
  textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit'
};

// Shared title + description block used by every HVHS destination
const PageHeader = ({ title, description, action }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1F2937', margin: '0 0 0.5rem 0', lineHeight: 1.2, textAlign: 'left' }}>
          {title}
        </h2>
        <p style={{ fontSize: '0.9375rem', color: '#6B7280', lineHeight: '1.6', margin: 0, maxWidth: '900px', textAlign: 'left' }}>
          {description}
        </p>
      </div>
      {action && <div style={{ flex: '0 0 auto', paddingTop: '1.5rem' }}>{action}</div>}
    </div>
  </div>
);

// ⓘ button + small pop-over with the framework paragraph. Keyboard operable; Escape and
// click-outside close it; focus returns to the button on close.
const FrameworkInfoButton = () => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = 'hvhs-framework-popover';

  const close = useCallback(() => {
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    if (popoverRef.current) popoverRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close]);

  return (
    <span style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="About the framework"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-haspopup="dialog"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '22px', height: '22px', borderRadius: '50%', border: '1px solid #113743',
          backgroundColor: open ? '#113743' : 'white', color: open ? 'white' : '#113743',
          fontSize: '0.8125rem', fontWeight: '700', lineHeight: 1, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic'
        }}
      >
        i
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label="About the framework"
          tabIndex={-1}
          style={{
            position: 'absolute', top: 'calc(100% + 0.5rem)', left: 0, zIndex: 50,
            width: 'min(420px, 80vw)', backgroundColor: 'white', border: '1px solid #E5E7EB',
            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.12)', padding: '1rem 1.25rem',
            textAlign: 'left', outline: 'none'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: '700', color: '#1F2937', margin: 0 }}>About the framework</h4>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6B7280', lineHeight: 1, padding: 0 }}
            >
              &times;
            </button>
          </div>
          <p style={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
            {FRAMEWORK_PARAGRAPH}
          </p>
        </div>
      )}
    </span>
  );
};

const HVHSPage = ({ initialView = 'map' }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [allCountriesData, setAllCountriesData] = useState([]);
  const [elementScoresData, setElementScoresData] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showWorldMap, setShowWorldMap] = useState(true);
  const [parsedData, setParsedData] = useState(null);
  const [normalizedScores, setNormalizedScores] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [pendingCountrySelection, setPendingCountrySelection] = useState(null);
  const [hoveredComponent, setHoveredComponent] = useState(null); // { index, name }
  const [isPanelLocked, setIsPanelLocked] = useState(false);
  const [mapScoreType, setMapScoreType] = useState('Overall');
  const [showBenchmarking, setShowBenchmarking] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [metadataData, setMetadataData] = useState([]);
  const [showDataModal, setShowDataModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // Refs
  const worldMapRef = useRef(null);
  const radialChartRef = useRef(null);
  const elementBarChartRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Respond to initialView prop changes
  useEffect(() => {
    if (initialView === 'map') {
      setShowWorldMap(true);
      setShowBenchmarking(false);
      setShowAbout(false);
    } else if (initialView === 'profile') {
      setShowWorldMap(false);
      setShowBenchmarking(false);
      setShowAbout(false);
    } else if (initialView === 'benchmarking') {
      setShowBenchmarking(true);
      setShowAbout(false);
    } else if (initialView === 'about') {
      setShowAbout(true);
      setShowBenchmarking(false);
    }
  }, [initialView]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [data, elemData, metaData] = await Promise.all([
        HVHSViz.loadAllCountriesData(),
        HVHSViz.loadElementScores(),
        HVHSViz.loadMetadata()
      ]);
      setAllCountriesData(data);
      setElementScoresData(elemData);
      setMetadataData(metaData);

      const countries = HVHSViz.getAvailableCountries(data);
      const years = HVHSViz.getAvailableYears(data);
      setAvailableCountries(countries);
      setAvailableYears(years);
      // Country Profile defaults to the USA on first load; falls back to the empty
      // "Select a country" state if that name is absent from the dataset.
      if (countries.includes(DEFAULT_PROFILE_COUNTRY)) setSelectedCountry(DEFAULT_PROFILE_COUNTRY);

      if (years.includes('2023')) setSelectedYear('2023');
      else if (years.length > 0) setSelectedYear(years[years.length - 1]);
    } catch (error) {
      console.error('Error loading HVHS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryClickFromMap = useCallback((country) => {
    setPendingCountrySelection(country);
  }, []);

  // Render world map
  useEffect(() => {
    if (showWorldMap && worldMapRef.current && allCountriesData.length > 0) {
      HVHSViz.renderWorldMap(worldMapRef.current, allCountriesData, handleCountryClickFromMap, mapScoreType);
    }
  }, [showWorldMap, allCountriesData, handleCountryClickFromMap, mapScoreType]);

  // Handle pending country selection
  useEffect(() => {
    if (pendingCountrySelection) {
      setSelectedCountry(pendingCountrySelection);
      setShowWorldMap(false);
      setHoveredComponent(null);
      setIsPanelLocked(false);
      setPendingCountrySelection(null);
    }
  }, [pendingCountrySelection]);

  // Load country data when selection changes
  useEffect(() => {
    if (!selectedCountry) {
      setParsedData(null);
      setNormalizedScores([]);
      setCountryData([]);
      return;
    }
    if (selectedYear && !showWorldMap) {
      loadCountryData();
    }
  }, [selectedCountry, selectedYear, showWorldMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCountryData = () => {
    if (!selectedCountry || !selectedYear) return;
    const filteredCountryData = HVHSViz.filterDataByCountry(allCountriesData, selectedCountry);
    setCountryData(filteredCountryData);
    const parsed = HVHSViz.parseFrameworkData(filteredCountryData, selectedYear);
    setParsedData(parsed);
    const normalized = HVHSViz.getAveragedNormalizedData(parsed.scores, parsed.elements);
    setNormalizedScores(normalized);
  };

  // Handle hover from radial chart
  const handleHoverComponent = useCallback((index, sectionName) => {
    if (!isPanelLocked) {
      setHoveredComponent({ index, name: sectionName });
    }
  }, [isPanelLocked]);

  // Render radial chart
  useEffect(() => {
    if (parsedData && normalizedScores.length > 0 && radialChartRef.current && !showWorldMap) {
      const timer = setTimeout(() => {
        HVHSViz.renderRadialChart(
          radialChartRef.current,
          normalizedScores,
          HVHSViz.section_names,
          handleHoverComponent
        );
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [parsedData, normalizedScores, showWorldMap, handleHoverComponent]);

  // Render element bar chart when hovered component changes
  useEffect(() => {
    if (hoveredComponent && elementBarChartRef.current && selectedCountry) {
      const elemScores = HVHSViz.getElementScoresForComponent(
        elementScoresData, selectedCountry, hoveredComponent.index
      );
      // Data-driven: show only sub-elements that actually have a score (Che, 2026-06:
      // drop sub-elements with no data from the visualizer rather than showing N/A).
      const scored = elemScores.filter(e => e.score !== null && !isNaN(e.score));
      HVHSViz.renderElementBarChart(elementBarChartRef.current, scored, hoveredComponent.name);
    }
  }, [hoveredComponent, elementScoresData, selectedCountry]);

  // Listen for radial chart section clicks to lock the component
  useEffect(() => {
    const handleSectionClick = (e) => {
      const { index, section } = e.detail;
      setHoveredComponent({ index, name: section });
      setIsPanelLocked(true);
    };
    document.addEventListener('hvhsSectionClick', handleSectionClick);
    return () => document.removeEventListener('hvhsSectionClick', handleSectionClick);
  }, []);

  // Lock panel when mouse enters the right panel, unlock when it leaves
  const handleRightPanelEnter = () => {
    setIsPanelLocked(true);
  };

  const handleRightPanelLeave = () => {
    // Only unlock if not click-locked
    if (!isPanelLocked) {
      setHoveredComponent(null);
    }
  };

  const handleBackToMap = () => {
    setShowWorldMap(true);
    setHoveredComponent(null);
    setIsPanelLocked(false);
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country || '');
    setHoveredComponent(null);
    setIsPanelLocked(false);
  };

  const goToAbout = () => {
    setShowAbout(true);
    setShowBenchmarking(false);
  };

  const scoreBands = Array.isArray(HVHSViz.HVHS_SCORE_BANDS) ? HVHSViz.HVHS_SCORE_BANDS : [];
  const missingColor = HVHSViz.HVHS_MISSING_COLOR || '#D1D5DB';
  const countryCoverage = availableCountries.length || HVHS_COUNTRY_COVERAGE;
  const componentCount = HVHSViz.section_names.length;
  const contributingComponents = parsedData
    ? HVHSViz.section_names.filter(name => parsedData.elements.includes(name)).length
    : 0;
  const hasComponentScore = (name) => Boolean(parsedData && parsedData.elements.includes(name));

  if (loading) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '3rem', textAlign: 'center', color: '#6B7280' }}>
        Loading High Value Health Systems data...
      </div>
    );
  }

  const renderBackButton = (label, onClick) => (
    <button
      onClick={onClick}
      style={backButtonStyle}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6B7280'}
    >
      &larr; {label}
    </button>
  );

  // Show About the Project page


  // Show Benchmarking Matrix page (renders its own header)
  if (showBenchmarking) {
    return <BenchmarkingMatrix onBack={() => { setShowBenchmarking(false); setShowWorldMap(true); }} onCountryClick={(country) => {
      setSelectedCountry(country);
      setShowBenchmarking(false);
      setShowWorldMap(false);
      setHoveredComponent(null);
      setIsPanelLocked(false);
    }} />;
  }

  const activateComponent = (i, name) => {
    setHoveredComponent({ index: i, name });
    setIsPanelLocked(true);
  };

  // Build default table for right panel
  const renderDefaultTable = () => (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1F2937', margin: '0 0 0.5rem 0' }}>
        Component Scores
      </h3>
      <p style={{ fontSize: '0.8125rem', color: '#6B7280', margin: '0 0 0.5rem 0' }}>
        {contributingComponents} of {componentCount} components contribute to this profile. A score of 0 is the lowest point on the 0&ndash;5 scale; N/A means no component score is available for this country.
      </p>
      <p style={{ fontSize: '0.8125rem', color: '#6B7280', margin: '0 0 1rem 0' }}>
        Hover over, focus, or click a component on the radial plot or in this table to see element-level details.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#7C3AED', fontWeight: '600', borderBottom: '2px solid #E5E7EB' }}>Component</th>
            <th scope="col" style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#7C3AED', fontWeight: '600', borderBottom: '2px solid #E5E7EB' }}>Name</th>
            <th scope="col" style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#7C3AED', fontWeight: '600', borderBottom: '2px solid #E5E7EB' }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {HVHSViz.section_names.map((name, i) => {
            const score = normalizedScores[i];
            const hasData = hasComponentScore(name) && score !== null && score !== undefined && !isNaN(score);
            return (
              <tr
                key={name}
                tabIndex={0}
                aria-label={`C${i + 1}: ${name}, score ${hasData ? score.toFixed(2) : 'not available'}. Press Enter to view element details.`}
                style={{
                  borderBottom: '1px solid #F3F4F6',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                  handleHoverComponent(i, name);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                  handleHoverComponent(i, name);
                }}
                onBlur={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
                onClick={() => activateComponent(i, name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateComponent(i, name); }
                }}
              >
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: '500', color: '#374151' }}>C{i + 1}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: '#4B5563' }}>{name}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: '500', color: hasData ? '#1F2937' : '#9CA3AF' }}>
                  {hasData ? score.toFixed(2) : 'N/A'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Build hovered component detail panel
  const renderComponentDetail = () => {
    if (!hoveredComponent) return null;
    const { index, name } = hoveredComponent;
    // Score chip removed per CR feedback 27 Apr — score still surfaces in the radial + bar chart.
    return (
      <div
        ref={rightPanelRef}
        onMouseEnter={handleRightPanelEnter}
        onMouseLeave={handleRightPanelLeave}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Top half: Component title + description (score chip removed per CR feedback 27 Apr) */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#1F2937', margin: '0 0 0.5rem 0' }}>
              C{index + 1}: {name}
            </h3>
            <button
              type="button"
              onClick={() => { setHoveredComponent(null); setIsPanelLocked(false); }}
              style={{ ...linkButtonStyle, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
            >
              All components
            </button>
          </div>
          <p style={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
            {HVHSViz.COMPONENT_DESCRIPTIONS[name] || ''}
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '0.5rem 0 1rem 0' }} />

        {/* Bottom half: Element bar chart */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#374151', margin: '0 0 0.5rem 0' }}>
            Element Scores
          </h4>
          <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0 0 0.5rem 0' }}>
            Hover over, click, or tap a bar to see the element definition. Full definitions are listed under Show Metadata.
          </p>
          <div
            ref={elementBarChartRef}
            style={{
              width: '100%',
              backgroundColor: 'white',
              borderRadius: '6px'
            }}
          />
        </div>
      </div>
    );
  };

  // Export the selected country's element scores as a CSV download
  const downloadElementScoresCsv = () => {
    const rows = elementScoresData
      .filter(r => r.Country === selectedCountry)
      .sort((a, b) => (Number(a.Component) - Number(b.Component)) || a.Element.localeCompare(b.Element));
    const esc = (v) => /[",\n]/.test(String(v ?? '')) ? `"${String(v).replace(/"/g, '""')}"` : String(v ?? '');
    const lines = ['Country,Component,Element,Element Score'];
    rows.forEach(r => lines.push([r.Country, r.Component, r.Element, r['Element Score']].map(esc).join(',')));
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HVHS_${(selectedCountry || 'country').replace(/[^a-z0-9]+/gi, '_')}_element_scores.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Shared modal shell
  const Modal = ({ title, onClose, children, footer }) => (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: 'white', borderRadius: '12px', maxWidth: '1100px', width: '100%',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1F2937', margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', overflow: 'auto', textAlign: 'left' }}>{children}</div>
        {footer && <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>{footer}</div>}
      </div>
    </div>
  );

  const thStyle = { textAlign: 'left', padding: '0.5rem 0.75rem', color: '#113743', fontWeight: '600', borderBottom: '2px solid #E5E7EB', position: 'sticky', top: 0, backgroundColor: 'white', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.5rem 0.75rem', color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };

  // Show Data: the selected country's element scores
  const renderDataModal = () => {
    const rows = elementScoresData
      .filter(r => r.Country === selectedCountry)
      .sort((a, b) => (Number(a.Component) - Number(b.Component)) || a.Element.localeCompare(b.Element));
    return (
      <Modal
        title={`${selectedCountry} — Element Scores`}
        onClose={() => setShowDataModal(false)}
        footer={
          <button onClick={downloadElementScoresCsv} style={{ padding: '0.5rem 1rem', backgroundColor: '#113743', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
            Download CSV
          </button>
        }
      >
        {rows.length === 0 ? (
          <p style={{ color: '#6B7280' }}>No element-level scores available for {selectedCountry}.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead><tr><th scope="col" style={thStyle}>Component</th><th scope="col" style={thStyle}>Sub-element</th><th scope="col" style={{ ...thStyle, textAlign: 'right' }}>Score (0–5)</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={tdStyle}>C{r.Component}</td>
                  <td style={tdStyle}>{r.Element}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{r['Element Score']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    );
  };

  // Show Metadata: per-indicator metadata (source, definition, unit, etc.)
  const renderMetadataModal = () => {
    const cols = [
      ['Component', 'Component'], ['Element', 'Sub-element'], ['Variable', 'Indicator'],
      ['Source', 'Source'], ['Variable Definition', 'Definition'], ['Unit of Measure', 'Unit'],
      ['Date Range', 'Date range'], ['Scale Direction', 'Scale direction']
    ];
    return (
      <Modal title="Indicator Metadata" onClose={() => setShowMetadataModal(false)}>
        {metadataData.length === 0 ? (
          <p style={{ color: '#6B7280' }}>Metadata not available.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr>{cols.map(([, label]) => <th key={label} scope="col" style={thStyle}>{label}</th>)}</tr></thead>
              <tbody>
                {metadataData.filter(r => r.Element || r.Variable).map((r, i) => (
                  <tr key={i}>{cols.map(([key]) => (
                    <td key={key} style={{ ...tdStyle, minWidth: key === 'Variable Definition' ? '320px' : 'auto' }}>
                      {key === 'URL' && r[key] ? <a href={r[key]} target="_blank" rel="noopener noreferrer">link</a> : (r[key] || '')}
                    </td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    );
  };

  const mapScoreLabel = mapScoreType === 'Overall'
    ? 'Overall high value health system score'
    : `C${HVHSViz.section_names.indexOf(mapScoreType) + 1}: ${mapScoreType}`;

  const renderMapDescription = () => (
    <p style={{ fontSize: '0.875rem', color: '#4B5563', lineHeight: '1.7', margin: '0 0 1.25rem 0', textAlign: 'left' }}>
      Read more about the framework, scoring, and data sources in{' '}
      <button type="button" onClick={goToAbout} style={linkButtonStyle}>About the Project</button>.
    </p>
  );

  const renderAboutModal = () => (
    <Modal title={PAGE_COPY.about.title} onClose={() => setShowAbout(false)}>
      <p style={{ fontSize: '0.9375rem', color: '#6B7280', lineHeight: '1.6', margin: '0 0 1.25rem 0', textAlign: 'left' }}>
        {PAGE_COPY.about.description}
      </p>
      <div style={{ maxWidth: '800px', lineHeight: '1.8', color: '#374151' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1F2937', margin: '0 0 1rem 0' }}>
          Transitioning to High-Value Health Systems
        </h3>
        <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
          The High Value Health Systems project by the Health Systems Innovation Lab at the Harvard T.H. Chan School of Public Health develops a comprehensive, data-driven framework for evaluating and benchmarking health systems across {HVHS_COUNTRY_COVERAGE} countries.
        </p>
        <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
          {FRAMEWORK_PARAGRAPH}
        </p>

        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1F2937', margin: '2rem 0 1rem 0' }}>
          Methodology
        </h3>
        <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
          Countries are scored on a 0&ndash;5 scale for each component based on curated indicators from international datasets, expert assessments, and policy analysis. Scores are normalized and aggregated to produce an overall high value health system score that enables cross-country comparison and benchmarking. Component scores are shown as N/A where no score is available for a country.
        </p>

        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1F2937', margin: '2rem 0 1rem 0' }}>
          Data Coverage
        </h3>
        <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
          The current release covers {HVHS_COUNTRY_COVERAGE} countries and was last updated in {HVHS_DATA_UPDATED}. Indicator sources, definitions, units, and date ranges are listed under Show Metadata on each Country Profile.
        </p>

        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1F2937', margin: '2rem 0 1rem 0' }}>
          Further Reading
        </h3>
        <ul style={{ paddingLeft: '1.5rem', fontSize: '1rem', marginBottom: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://hsph.harvard.edu/research/health-systems-innovation-lab/work/transitioning-to-high-value-health-systems-in-g20-countries/"
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#113743', textDecoration: 'underline' }}>
              Transitioning to High-Value Health Systems in G20+ Countries
            </a>
          </li>
          <li>
            <a href="https://hsph.harvard.edu/research/health-systems-innovation-lab/"
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#113743', textDecoration: 'underline' }}>
              Health Systems Innovation Lab at Harvard T.H. Chan School of Public Health
            </a>
          </li>
        </ul>
      </div>
    </Modal>
  );

  const profileActionButtonStyle = (disabled) => ({
    padding: '0.625rem 1.25rem', fontSize: '0.9375rem', fontWeight: '500',
    backgroundColor: disabled ? '#E5E7EB' : '#1F2937', color: disabled ? '#9CA3AF' : 'white',
    border: 'none', borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s', whiteSpace: 'nowrap'
  });

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {showDataModal && renderDataModal()}
      {showMetadataModal && renderMetadataModal()}
      {showAbout && renderAboutModal()}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header */}
        <PageHeader
          title={showWorldMap ? PAGE_COPY.map.title : PAGE_COPY.profile.title}
          description={showWorldMap ? PAGE_COPY.map.description : PAGE_COPY.profile.description}
          action={!showWorldMap ? renderBackButton('Back to World Map', handleBackToMap) : null}
        />

        {/* World Map View */}
        {showWorldMap && (
          <>
            {renderMapDescription()}
            <div style={{ backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  {mapScoreType === 'Overall' ? 'Overall Score' : mapScoreType}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label htmlFor="hvhs-map-score-select" style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Score shown</label>
                  <select
                    id="hvhs-map-score-select"
                    value={mapScoreType}
                    onChange={(e) => setMapScoreType(e.target.value)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      minWidth: '220px'
                    }}
                  >
                    <option value="Overall">Overall Score</option>
                    {HVHSViz.section_names.map((name, i) => (
                      <option key={name} value={name}>C{i + 1}: {name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {allCountriesData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#EF4444' }}>No data loaded.</div>
              ) : (
                <div ref={worldMapRef} id="hvhs-world-map" style={{ width: '100%', backgroundColor: 'white', borderRadius: '6px', padding: '1rem' }} />
              )}
            </div>
          </>
        )}

        {/* Country Detail View - Two-Panel Layout */}
        {!showWorldMap && (
          <>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', lineHeight: '1.7', margin: '0 0 1.25rem 0', maxWidth: '900px', textAlign: 'left' }}>
              <strong style={{ color: '#1F2937' }}>Show Data</strong> lists the element scores behind this profile and offers a CSV download.{' '}
              <strong style={{ color: '#1F2937' }}>Show Metadata</strong> lists each indicator&rsquo;s source, definition, unit, date range, and scale direction.{' '}
              <strong style={{ color: '#1F2937' }}>Download Primer</strong> will provide the methodology primer and is coming soon.
            </p>

            {/* Country selector + Action Buttons — same row */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 auto' }}>
                <label htmlFor="hvhs-country-search" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Select Country
                </label>
                <CountryTypeahead
                  inputId="hvhs-country-search"
                  countries={availableCountries}
                  value={selectedCountry}
                  onSelect={handleSelectCountry}
                />
              </div>
              {/* Reporting year selector removed per CR feedback (27 Apr); selectedYear still
                  resolved automatically via useEffect (defaults to 2023, then last available). */}
              <div style={{ flex: 1 }} />
              <div role="group" aria-label="Evidence and methodology" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'Show Data', onClick: () => setShowDataModal(true), disabled: !selectedCountry, title: selectedCountry ? `Element scores for ${selectedCountry}` : 'Select a country to view its data' },
                  { label: 'Show Metadata', onClick: () => setShowMetadataModal(true), disabled: false, title: 'Indicator sources, definitions, units, and coverage' },
                  { label: 'Download Primer', onClick: () => {}, disabled: true, title: 'Methodology primer — coming soon', suffix: 'coming soon' }
                ].map(({ label, onClick, disabled, title, suffix }) => (
                  <button key={label} type="button" onClick={onClick} disabled={disabled} aria-disabled={disabled} title={title}
                    style={profileActionButtonStyle(disabled)}
                    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = '#374151'; }}
                    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = '#1F2937'; }}
                  >
                    {label}{suffix && <span style={{ fontSize: '0.75rem', fontWeight: '400', marginLeft: '0.375rem' }}>({suffix})</span>}
                  </button>
                ))}
              </div>
            </div>

            {!selectedCountry || !parsedData ? (
              <div style={{
                backgroundColor: '#f9fafb', padding: '3rem 1.5rem', borderRadius: '8px',
                textAlign: 'left', minHeight: '320px', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  Select a country
                </h3>
                <p style={{ fontSize: '0.9375rem', color: '#6B7280', margin: 0, maxWidth: '520px', lineHeight: '1.6' }}>
                  Search for one of {countryCoverage} countries above, or click a country on the{' '}
                  <button type="button" onClick={handleBackToMap} style={linkButtonStyle}>{PAGE_COPY.map.title}</button>, to view its component and element scores.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                alignItems: 'stretch',
                minHeight: '620px'
              }}>
                {/* LEFT PANEL: Radial Chart */}
                <div style={{
                textAlign: 'left',
                  flex: '0 0 560px',
                  backgroundColor: '#f9fafb',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* R6: Country title above radial plot — score removed per CR feedback (27 Apr) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', margin: '0 0 1rem 0' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                      {selectedCountry}
                    </h3>
                    <span
                      title={`${contributingComponents} of ${componentCount} high value health system components have a score for ${selectedCountry}`}
                      style={{
                        fontSize: '0.8125rem', fontWeight: '600', color: '#113743',
                        border: '1px solid #113743', borderRadius: '9999px', padding: '0.25rem 0.75rem',
                        backgroundColor: 'white', whiteSpace: 'nowrap'
                      }}
                    >
                      Coverage: {contributingComponents} of {componentCount} components
                    </span>
                  </div>
                  <div
                    ref={radialChartRef}
                    id="hvhs-radial-chart"
                    style={{
                      width: '100%',
                      flex: 1,
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}
                  />
                </div>

                {/* RIGHT PANEL */}
                <div style={{
                textAlign: 'left',
                  flex: 1,
                  backgroundColor: '#f9fafb',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  minWidth: 0,
                  overflow: 'auto'
                }}>
                  {hoveredComponent ? renderComponentDetail() : renderDefaultTable()}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HVHSPage;
